export type QueueStatus = "waiting" | "preparing" | "called" | "in_service" | string;

export function getMobileAgendaQuickStartHour(now: Date) {
  return Math.min(Math.max(now.getHours(), 7), 20);
}

export function getMobileAgendaQuickStartSlot(now: Date) {
  const operationalHour = getMobileAgendaQuickStartHour(now);
  if (now.getHours() < 7 || now.getHours() >= 20) {
    return { hour: operationalHour, minute: 0 };
  }

  const nextHalfHour = now.getMinutes() < 30 ? 30 : 60;
  if (nextHalfHour === 60) {
    return { hour: Math.min(operationalHour + 1, 20), minute: 0 };
  }

  return { hour: operationalHour, minute: nextHalfHour };
}

export function getMobileAgendaNowScrollTop(now: Date, startHour: number, slotHeight: number) {
  const currentHour = now.getHours() + now.getMinutes() / 60;
  return Math.max(0, (currentHour - startHour) * slotHeight - slotHeight);
}

export function getMobileAppointmentTimingSignal(input: {
  status: string;
  startTime: Date | string | number;
  endTime: Date | string | number;
}, now = new Date()) {
  if (["cancelled", "completed", "no_show"].includes(input.status)) return null;

  const startAt = new Date(input.startTime).getTime();
  const endAt = new Date(input.endTime).getTime();
  const nowAt = now.getTime();
  if (!Number.isFinite(startAt) || !Number.isFinite(endAt)) return null;

  if (startAt <= nowAt && nowAt <= endAt) {
    return { label: "Em atendimento", description: "Atualize para concluído quando o serviço terminar.", tone: "active" as const, compact: "●" };
  }
  if (endAt < nowAt) {
    return { label: "Horário atrasado", description: "Registre conclusão, falta ou ajuste o horário deste atendimento.", tone: "overdue" as const, compact: "!" };
  }
  if (startAt - nowAt <= 15 * 60_000) {
    return { label: "Próximo em breve", description: "Prepare o atendimento e confirme a chegada da cliente.", tone: "upcoming" as const, compact: "↗" };
  }
  return null;
}

export function isMobileSaleActionDisabled(input: {
  productCount: number;
  requiresBranch: boolean;
  hasBranch: boolean;
  isProfessionalConsumption: boolean;
  hasProductsWithoutCost: boolean;
  isBalanced: boolean;
  isSubmitting: boolean;
}) {
  return input.productCount === 0
    || (input.requiresBranch && !input.hasBranch)
    || (input.isProfessionalConsumption && input.hasProductsWithoutCost)
    || (!input.isProfessionalConsumption && !input.isBalanced)
    || input.isSubmitting;
}

export function getNextMobilePendingPayment<T extends { startTime: Date | string | number }>(payments: T[]) {
  return payments.reduce<T | undefined>((nextPayment, payment) => {
    if (!nextPayment) return payment;

    const nextTime = new Date(nextPayment.startTime).getTime();
    const paymentTime = new Date(payment.startTime).getTime();
    if (!Number.isFinite(paymentTime)) return nextPayment;
    if (!Number.isFinite(nextTime) || paymentTime < nextTime) return payment;
    return nextPayment;
  }, undefined);
}

export function getMobileProfessionalTurnCountdown(startTime: Date | string | number, now = new Date()) {
  const differenceInMinutes = Math.ceil((new Date(startTime).getTime() - now.getTime()) / 60_000);

  if (!Number.isFinite(differenceInMinutes)) {
    return { label: "Horário a confirmar", minutesUntil: null, tone: "muted" as const };
  }
  if (differenceInMinutes <= 0) {
    return { label: "Horário em andamento", minutesUntil: differenceInMinutes, tone: "active" as const };
  }
  if (differenceInMinutes === 1) {
    return { label: "Em 1 minuto", minutesUntil: differenceInMinutes, tone: "urgent" as const };
  }
  if (differenceInMinutes <= 15) {
    return { label: `Em ${differenceInMinutes} min`, minutesUntil: differenceInMinutes, tone: "urgent" as const };
  }
  return { label: `Em ${differenceInMinutes} min`, minutesUntil: differenceInMinutes, tone: "scheduled" as const };
}

type MobileQueueAlertItem = { status: QueueStatus; preparationStartedAt?: Date | string | number | null };
type MobileAppointmentAlertItem = { status: string; startTime: Date | string | number };
type MobileOperationalRole = "admin" | "recepcionista" | "caixa" | "funcionario";

export function getMobileOperationalAlerts(input: {
  staffRole: MobileOperationalRole;
  queue: MobileQueueAlertItem[];
  appointments: MobileAppointmentAlertItem[];
  now?: Date;
}) {
  if (input.staffRole === "funcionario") return [];

  const now = input.now ?? new Date();
  const nowTimestamp = now.getTime();
  const twoHoursFromNow = nowTimestamp + 2 * 60 * 60 * 1000;
  const waitingCount = input.queue.filter((item) => item.status === "waiting" || item.status === "called").length;
  const delayedPreparationCount = input.queue.filter((item) => {
    if (item.status !== "preparing" || !item.preparationStartedAt) return false;
    return nowTimestamp - new Date(item.preparationStartedAt).getTime() >= 25 * 60 * 1000;
  }).length;
  const pendingConfirmations = input.appointments.filter((appointment) => {
    const timestamp = new Date(appointment.startTime).getTime();
    return appointment.status === "pending" && timestamp >= nowTimestamp && timestamp <= twoHoursFromNow;
  }).length;
  const delayedAppointments = input.appointments.filter((appointment) => {
    const timestamp = new Date(appointment.startTime).getTime();
    return (appointment.status === "pending" || appointment.status === "confirmed") && timestamp < nowTimestamp;
  }).length;

  return [
    waitingCount > 0 ? { id: "arrival", count: waitingCount, title: `${waitingCount} chegada${waitingCount > 1 ? "s" : ""} aguardando`, description: "Revise a fila antes do próximo atendimento.", path: "/painel-chamada", tone: "primary" as const } : null,
    delayedPreparationCount > 0 ? { id: "preparation", count: delayedPreparationCount, title: `${delayedPreparationCount} preparação(ões) em atraso`, description: "Confira a responsável e atualize a fila.", path: "/painel-chamada", tone: "danger" as const } : null,
    pendingConfirmations > 0 ? { id: "confirmation", count: pendingConfirmations, title: `${pendingConfirmations} confirmação(ões) nas próximas 2h`, description: "Confirme os próximos horários da agenda.", path: "/weekly-calendar", tone: "warning" as const } : null,
    delayedAppointments > 0 ? { id: "late", count: delayedAppointments, title: `${delayedAppointments} horário(s) sem atualização`, description: "Registre conclusão, falta ou novo horário.", path: "/weekly-calendar", tone: "danger" as const } : null,
  ].filter(Boolean).slice(0, 2);
}

export function summarizeMobileQueueStatuses<T extends { status: QueueStatus }>(items: T[]) {
  return {
    waiting: items.filter((item) => item.status === "waiting"),
    preparing: items.filter((item) => item.status === "preparing"),
    called: items.filter((item) => item.status === "called"),
    inService: items.filter((item) => item.status === "in_service"),
  };
}
